import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import {
  AD_LIBRARY_CONTENT_TYPE,
  TOOL_LIBRARY_CONTENT_TYPE,
  addContentToAdLibraryAPI,
  addToolToLibraryAPI,
  getAdLibraryContentAPI,
  parseAddContentToAdLibraryResponse,
  parseAddToolToLibraryResponse,
  parseGetAdLibraryContentResponse,
} from '../../context-api/api-services/AdLibrary.service';
import { getManagingBranchNameForApi } from '../../utils/managingBranchName';
import AtomicButton from '../../atomic/button/Button';
import { AdLibraryAddModal } from './AdLibraryAddModal';
import './AdLibrary.css';

const ADD_BUTTON_STYLE = {
  borderRadius: '5px',
  width: '170px',
  height: '42px',
  fontWeight: '600',
  letterSpacing: '0.3px',
  marginTop: 0,
  marginBottom: 0,
};

const AD_PREVIEW_HOVER_DELAY_MS = 800;

const TABS = [
  { key: 'add', label: 'Add', Icon: ImageOutlinedIcon },
  { key: 'tools', label: 'Tools', Icon: ConstructionOutlinedIcon },
];

function isToolItem(item) {
  return item.contentType === TOOL_LIBRARY_CONTENT_TYPE;
}

function isAddTabItem(item) {
  return item.contentType === AD_LIBRARY_CONTENT_TYPE;
}

function getAdPreviewImageUrl(visual) {
  if (!visual) return null;
  if (visual.type === 'image') {
    return visual.accessUrl || visual.thumbnailUrl || null;
  }
  return visual.thumbnailUrl || visual.accessUrl || null;
}

function buildAdPreviewState(element, visual) {
  const imageUrl = getAdPreviewImageUrl(visual);
  if (!imageUrl || !element) return null;

  const thumb =
    element.querySelector('.ad-library-visual-placeholder') ||
    element;
  const rect = thumb.getBoundingClientRect();

  const targetWidth = Math.min(window.innerWidth * 0.88, 720);
  const targetHeight = Math.min(window.innerHeight * 0.72, 560);
  const scale = Math.max(rect.width / targetWidth, rect.height / targetHeight, 0.06);

  const originCenterX = rect.left + rect.width / 2;
  const originCenterY = rect.top + rect.height / 2;

  return {
    visual,
    translateX: originCenterX - window.innerWidth / 2,
    translateY: originCenterY - window.innerHeight / 2,
    scale,
  };
}

const AdLibrary = () => {
  const branchName = getManagingBranchNameForApi();

  const [activeTab, setActiveTab] = useState('add');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [previewAd, setPreviewAd] = useState(null);
  const previewHoverTimerRef = useRef(null);
  const previewHoverTargetRef = useRef(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');

  const addTabItems = useMemo(
    () => items.filter(isAddTabItem),
    [items]
  );

  const toolItems = useMemo(
    () => items.filter(isToolItem),
    [items]
  );

  const loadContent = useCallback(async () => {
    if (!branchName) {
      setLoading(false);
      setFetchError('Branch name is required to load ad library content.');
      setItems([]);
      return;
    }

    setLoading(true);
    setFetchError('');

    const response = await getAdLibraryContentAPI({ branchName });
    console.log('GetAdLibraryContent response', response);

    const parsed = parseGetAdLibraryContentResponse(response);
    if (!parsed.ok) {
      setFetchError(parsed.message);
      setItems([]);
    } else {
      setItems(parsed.items);
    }

    setLoading(false);
  }, [branchName]);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const clearPreviewHoverTimer = useCallback(() => {
    if (previewHoverTimerRef.current) {
      clearTimeout(previewHoverTimerRef.current);
      previewHoverTimerRef.current = null;
    }
  }, []);

  const cancelAdPreview = useCallback(() => {
    clearPreviewHoverTimer();
    previewHoverTargetRef.current = null;
    setPreviewAd(null);
  }, [clearPreviewHoverTimer]);

  const scheduleAdPreview = useCallback((element, visual) => {
    clearPreviewHoverTimer();
    setPreviewAd(null);
    previewHoverTargetRef.current = element;

    previewHoverTimerRef.current = setTimeout(() => {
      const target = previewHoverTargetRef.current;
      const nextPreview = buildAdPreviewState(target, visual);
      if (nextPreview) setPreviewAd(nextPreview);
      previewHoverTimerRef.current = null;
    }, AD_PREVIEW_HOVER_DELAY_MS);
  }, [clearPreviewHoverTimer]);

  useEffect(() => {
    cancelAdPreview();
  }, [activeTab, addModalOpen, cancelAdPreview]);

  useEffect(() => () => clearPreviewHoverTimer(), [clearPreviewHoverTimer]);

  const handleOpenAddModal = () => {
    setAddError('');
    setAddSuccess('');
    setAddModalOpen(true);
  };

  const handleAddSubmit = async (formValues) => {
    setAdding(true);
    setAddError('');
    setAddSuccess('');

    const isTool = formValues.kind === 'tool';
    let response;
    let parsed;

    if (isTool) {
      const { kind, ...toolPayload } = formValues;
      response = await addToolToLibraryAPI({
        managing_branch_name: branchName,
        ...toolPayload,
      });
      console.log('AddToolToLibrary response', response);
      parsed = parseAddToolToLibraryResponse(response);
    } else {
      const { kind, imageFile, ...adPayload } = formValues;
      if (imageFile) {
        console.log('Ad image selected for future upload API', imageFile);
      }
      response = await addContentToAdLibraryAPI({
        branchName,
        ...adPayload,
      });
      console.log('AddContentToAdLibrary response', response);
      parsed = parseAddContentToAdLibraryResponse(response);
    }

    setAdding(false);

    if (!parsed.ok) {
      setAddError(parsed.message);
      return;
    }

    setAddSuccess(parsed.message);
    setAddModalOpen(false);
    await loadContent();
  };

  const sectionTitle = activeTab === 'add' ? 'Ad Creatives' : 'Marketing Tools';

  const previewVisual = previewAd?.visual ?? null;
  const previewImageUrl = previewVisual ? getAdPreviewImageUrl(previewVisual) : null;

  return (
    <>
    <div style={styles.pageWrapper}>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.pageTitle}>Ad Library</h1>
          <p style={styles.pageSubtitle}>Manage your creatives, visuals, and marketing tools</p>
        </div>
        <AtomicButton
          label={activeTab === 'tools' ? 'ADD TOOL' : 'ADD VISUAL'}
          handleSubmit={handleOpenAddModal}
          style={ADD_BUTTON_STYLE}
        />
      </div>

      {addSuccess ? (
        <Alert severity="success" onClose={() => setAddSuccess('')} sx={{ mb: 2, borderRadius: '10px' }}>
          {addSuccess}
        </Alert>
      ) : null}

      {addError ? (
        <Alert severity="error" onClose={() => setAddError('')} sx={{ mb: 2, borderRadius: '10px' }}>
          {addError}
        </Alert>
      ) : null}

      {fetchError ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          {fetchError}
        </Alert>
      ) : null}

      <div className="ad-library-shell" style={styles.contentWrapper}>
        <aside className="ad-library-toc">
          <div className="ad-library-toc-header">Sections</div>
          {TABS.map((tab) => {
            const TabIcon = tab.Icon;
            return (
              <button
                key={tab.key}
                type="button"
                className={`ad-library-toc-link${activeTab === tab.key ? ' ad-library-toc-link--active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
                aria-current={activeTab === tab.key ? 'page' : undefined}
              >
                <TabIcon sx={{ fontSize: 15, flexShrink: 0 }} />
                {tab.label}
              </button>
            );
          })}
        </aside>

        <div style={styles.rightPanel}>
          <div style={styles.sectionHeader}>
            <span style={styles.sectionTitle}>{sectionTitle}</span>
            <span style={styles.countBadge}>
              {activeTab === 'tools' ? toolItems.length : addTabItems.length} items
            </span>
          </div>

          {loading ? (
            <div style={styles.loadingWrap}>
              <CircularProgress size={32} sx={{ color: '#024CAA' }} />
            </div>
          ) : activeTab === 'tools' ? (
            <div style={styles.toolsList}>
              {toolItems.length === 0 ? (
                <p style={styles.emptyText}>No marketing tools found for this branch.</p>
              ) : (
                toolItems.map((tool) => (
                  <div
                    key={tool.id}
                    className="ad-library-tool-card"
                    style={styles.toolCard}
                  >
                    <div style={styles.toolCardTop}>
                      <div style={styles.toolIconWrapper}>
                        <ConstructionOutlinedIcon style={{ fontSize: 22, color: '#EC8305' }} />
                      </div>
                      <div style={styles.toolHeaderInfo}>
                        <div style={styles.toolTitleRow}>
                          <p style={styles.toolName}>{tool.toolName}</p>
                          {tool.featured ? (
                            <span style={styles.featuredBadge}>Featured</span>
                          ) : null}
                          {tool.category ? (
                            <span style={styles.toolCategoryBadge}>{tool.category}</span>
                          ) : null}
                        </div>
                        <div style={styles.toolMetaRow}>
                          <span style={styles.toolMetaItem}>
                            Status: {tool.status ? 'Active' : 'Inactive'}
                          </span>
                          {tool.createdBy ? (
                            <span style={styles.toolMetaItem}>By {tool.createdBy}</span>
                          ) : null}
                          {tool.createdAt ? (
                            <span style={styles.toolMetaItem}>
                              {new Date(tool.createdAt).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </span>
                          ) : null}
                        </div>
                      </div>
                      {tool.websiteUrl ? (
                        <a
                          href={tool.websiteUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ad-library-go-to-btn"
                          style={styles.goToBtn}
                        >
                          <span>Go to Website</span>
                          <OpenInNewRoundedIcon style={{ fontSize: 14, marginLeft: 4 }} />
                        </a>
                      ) : null}
                    </div>

                    {tool.shortDescription ? (
                      <p style={styles.toolDescription}>{tool.shortDescription}</p>
                    ) : null}

                    {tool.notes ? (
                      <div style={styles.toolNotesBox}>
                        <span style={styles.toolNotesLabel}>Notes</span>
                        <p style={styles.toolNotesText}>{tool.notes}</p>
                      </div>
                    ) : null}
                  </div>
                ))
              )}
            </div>
          ) : (
            <div style={styles.visualsGrid}>
              {addTabItems.map((visual) => (
                <div
                  key={visual.id}
                  className="ad-library-visual-card"
                  style={styles.visualCard}
                  onMouseEnter={(event) => {
                    if (getAdPreviewImageUrl(visual)) {
                      scheduleAdPreview(event.currentTarget, visual);
                    }
                  }}
                  onMouseLeave={cancelAdPreview}
                  onClick={() => {
                    if (visual.accessUrl) window.open(visual.accessUrl, '_blank', 'noopener,noreferrer');
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && visual.accessUrl) {
                      window.open(visual.accessUrl, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <div className="ad-library-visual-placeholder" style={styles.visualPlaceholder}>
                    {visual.thumbnailUrl || (visual.type === 'image' && visual.accessUrl) ? (
                      <>
                        <img
                          src={visual.thumbnailUrl || visual.accessUrl}
                          alt={visual.title}
                          style={styles.visualThumbnail}
                        />
                        {visual.type === 'video' ? (
                          <div style={styles.videoOverlay}>
                            <PlayCircleOutlineRoundedIcon style={{ fontSize: 36, color: '#ffffff' }} />
                          </div>
                        ) : null}
                      </>
                    ) : visual.type === 'video' ? (
                      <PlayCircleOutlineRoundedIcon style={styles.visualPlaceholderIcon} />
                    ) : (
                      <ImageOutlinedIcon style={styles.visualPlaceholderIcon} />
                    )}
                  </div>

                  <div style={styles.visualCardFooter}>
                    <div style={styles.visualMeta}>
                      <span style={styles.visualLabel}>{visual.title}</span>
                      {visual.goodToUseOn.length > 0 ? (
                        <span style={styles.visualPlatforms}>
                          {visual.goodToUseOn.join(' · ')}
                        </span>
                      ) : null}
                    </div>
                    <span
                      style={{
                        ...styles.visualTypeBadge,
                        backgroundColor: visual.type === 'video' ? '#EFF6FF' : '#FFF7ED',
                        color: visual.type === 'video' ? '#024CAA' : '#EC8305',
                        border: `1px solid ${visual.type === 'video' ? '#BFDBFE' : '#FDE68A'}`,
                      }}
                    >
                      {visual.category}
                    </span>
                  </div>
                </div>
              ))}

              {!loading && addTabItems.length === 0 ? (
                <p style={{ ...styles.emptyText, gridColumn: '1 / -1' }}>
                  No content found in Add tab for this branch.
                </p>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <AdLibraryAddModal
        open={addModalOpen}
        activeTab={activeTab}
        onClose={() => {
          if (!adding) setAddModalOpen(false);
        }}
        onSubmit={handleAddSubmit}
        submitting={adding}
      />
    </div>

    {previewAd && previewVisual && previewImageUrl ? (
      <div className="ad-library-preview-overlay" aria-hidden="true">
        <div
          key={previewVisual.id}
          className="ad-library-preview-panel"
          style={{
            '--zoom-tx': `${previewAd.translateX}px`,
            '--zoom-ty': `${previewAd.translateY}px`,
            '--zoom-scale': previewAd.scale,
          }}
        >
          <div className="ad-library-preview-image-wrap">
            <img
              src={previewImageUrl}
              alt={previewVisual.title}
              className="ad-library-preview-image"
            />
            {previewVisual.type === 'video' ? (
              <div className="ad-library-preview-video-badge">
                <PlayCircleOutlineRoundedIcon style={{ fontSize: 40, color: '#ffffff' }} />
              </div>
            ) : null}
          </div>
          <p className="ad-library-preview-title">{previewVisual.title}</p>
        </div>
      </div>
    ) : null}
    </>
  );
};

const styles = {
  pageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    padding: '28px 32px',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    boxSizing: 'border-box',
    fontFamily: 'Inter Regular, Inter, sans-serif',
  },

  pageHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 28,
  },

  pageTitle: {
    margin: 0,
    fontSize: 24,
    fontFamily: 'Inter Bold, Inter, sans-serif',
    color: '#091057',
    lineHeight: 1.2,
  },

  pageSubtitle: {
    margin: '4px 0 0 0',
    fontSize: 13,
    color: '#6b7280',
    fontFamily: 'Inter Regular, Inter, sans-serif',
  },

  contentWrapper: {
    display: 'flex',
    flexDirection: 'row',
    gap: 16,
    alignItems: 'flex-start',
  },

  rightPanel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
  },

  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 14,
  },

  sectionTitle: {
    fontSize: 15,
    fontFamily: 'Inter Bold, Inter, sans-serif',
    color: '#091057',
  },

  countBadge: {
    fontSize: 11,
    fontFamily: 'Inter Regular, Inter, sans-serif',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    border: '1px solid #e5e7eb',
    borderRadius: 20,
    padding: '2px 8px',
  },

  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 180,
  },

  emptyText: {
    margin: 0,
    fontSize: 13,
    color: '#6b7280',
    fontFamily: 'Inter Regular, Inter, sans-serif',
  },

  visualsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
    gap: 16,
  },

  visualCard: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    overflow: 'hidden',
    cursor: 'pointer',
  },

  visualPlaceholder: {
    position: 'relative',
    width: '100%',
    minHeight: 263,
    aspectRatio: '3 / 4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    overflow: 'hidden',
  },

  visualThumbnail: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  videoOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(9, 16, 87, 0.25)',
  },

  visualPlaceholderIcon: {
    fontSize: 36,
    color: '#d1d5db',
  },

  visualCardFooter: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    padding: '8px 12px',
  },

  visualMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    flex: 1,
  },

  visualPlatforms: {
    fontSize: 10,
    fontFamily: 'Inter Regular, Inter, sans-serif',
    color: '#9ca3af',
    textTransform: 'capitalize',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  visualLabel: {
    fontSize: 12,
    fontFamily: 'Inter Regular, Inter, sans-serif',
    color: '#374151',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginRight: 8,
  },

  visualTypeBadge: {
    fontSize: 10,
    fontFamily: 'Inter Bold, Inter, sans-serif',
    borderRadius: 4,
    padding: '2px 6px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    flexShrink: 0,
  },

  toolsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },

  toolCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    padding: '16px 18px',
    cursor: 'default',
  },

  toolCardTop: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  },

  toolHeaderInfo: {
    flex: 1,
    minWidth: 0,
  },

  toolTitleRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },

  toolMetaRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },

  toolMetaItem: {
    fontSize: 11,
    fontFamily: 'Inter Regular, Inter, sans-serif',
    color: '#9ca3af',
  },

  featuredBadge: {
    fontSize: 10,
    fontFamily: 'Inter Bold, Inter, sans-serif',
    color: '#024CAA',
    backgroundColor: '#EFF6FF',
    border: '1px solid #BFDBFE',
    borderRadius: 4,
    padding: '2px 6px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },

  toolCategoryBadge: {
    fontSize: 10,
    fontFamily: 'Inter Bold, Inter, sans-serif',
    color: '#EC8305',
    backgroundColor: '#FFF7ED',
    border: '1px solid #FDE68A',
    borderRadius: 4,
    padding: '2px 6px',
  },

  toolNotesBox: {
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '10px 12px',
  },

  toolNotesLabel: {
    display: 'block',
    fontSize: 10,
    fontFamily: 'Inter Bold, Inter, sans-serif',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 4,
  },

  toolNotesText: {
    margin: 0,
    fontSize: 12,
    fontFamily: 'Inter Regular, Inter, sans-serif',
    color: '#4b5563',
    lineHeight: 1.5,
  },

  toolIconWrapper: {
    width: 40,
    height: 40,
    minWidth: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFF7ED',
    border: '1px solid #FDE68A',
    borderRadius: 8,
  },

  toolName: {
    margin: 0,
    fontSize: 15,
    fontFamily: 'Inter Bold, Inter, sans-serif',
    color: '#091057',
    lineHeight: 1.3,
  },

  toolDescription: {
    margin: 0,
    fontSize: 13,
    fontFamily: 'Inter Regular, Inter, sans-serif',
    color: '#6b7280',
    lineHeight: 1.55,
  },

  goToBtn: {
    display: 'flex',
    alignItems: 'center',
    padding: '7px 14px',
    backgroundColor: '#ffffff',
    color: '#024CAA',
    border: '1px solid #024CAA',
    borderRadius: 8,
    fontSize: 13,
    fontFamily: 'Inter Bold, Inter, sans-serif',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    transition: 'background 0.15s',
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
};

export default AdLibrary;
